import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Clock, Users, TrendingUp, AlertTriangle, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Firm-wide onboarding funnel time tile. Answers three questions at
 * a glance:
 *   1. How fast are we onboarding clients overall? (avg + median days)
 *   2. Which referrer converts fastest? (leaderboard, ascending avg)
 *   3. Who's stalled right now? (>14 days without completing)
 *
 * Read-only — driven off `GET /api/onboarding/analytics/summary`.
 */
export default function OnboardingAnalyticsTile() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all"); // 30d | 90d | ytd | all
  const [sendingDigest, setSendingDigest] = useState(false);
  const navigate = useNavigate();

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/onboarding/analytics/digest/send-now");
      if (r.data?.ok) {
        toast.success(`Digest emailed to ${r.data.admins?.length || 0} admin${r.data.admins?.length === 1 ? "" : "s"}.`);
      } else {
        toast.error(r.data?.error || "Digest could not be sent");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest send failed");
    } finally { setSendingDigest(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await api.get("/onboarding/analytics/summary", { params: { range } });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Could not load onboarding analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="onboarding-analytics-tile-loading">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-100 rounded w-1/3" />
          <div className="h-3 bg-slate-100 rounded w-1/2" />
          <div className="grid grid-cols-5 gap-3 mt-4">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded" />)}</div>
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-8" data-testid="onboarding-analytics-tile-error">
        <h3 className="text-base font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Onboarding funnel</h3>
        <p className="text-xs text-slate-500">{error || "No data available yet."}</p>
      </div>
    );
  }

  const o = data.overall || {};
  const hasData = (o.count || 0) > 0 || (data.in_progress_total || 0) > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-sm mb-8" data-testid="onboarding-analytics-tile">
      <header className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Onboarding funnel</h3>
          <p className="text-xs text-slate-500">Magic-link sent → fully signed off (TA form completed)</p>
        </div>
        <div className="flex items-center gap-1.5" data-testid="onboarding-range-chips">
          {[
            { k: "30d", label: "Last 30d" },
            { k: "90d", label: "Last 90d" },
            { k: "ytd", label: "YTD" },
            { k: "all", label: "All time" },
          ].map(chip => (
            <button key={chip.k} type="button" onClick={() => setRange(chip.k)}
              data-testid={`range-chip-${chip.k}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                range === chip.k
                  ? "bg-[#100f5d] text-white border-[#100f5d]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}>
              {chip.label}
            </button>
          ))}
          <button type="button" onClick={sendDigestNow} disabled={sendingDigest}
            data-testid="send-digest-now-btn" title="Send the weekly onboarding digest email to admin solicitors now"
            className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            <Send className="w-3 h-3" /> {sendingDigest ? "Sending…" : "Email digest"}
          </button>
        </div>
      </header>

      {!hasData ? (
        <p className="text-sm text-slate-500 italic py-6 text-center" data-testid="onboarding-analytics-empty">
          {range === "all"
            ? "No onboarding activity yet — the tile will populate once you invite your first client."
            : "No onboarding activity in this window. Try widening the range."}
        </p>
      ) : (
        <>
          {/* Overall stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            <Stat label="Completed" value={o.count} icon={TrendingUp} tone="emerald" />
            <Stat label="Avg days" value={o.avg_days} suffix="d" icon={Clock} tone="violet" />
            <Stat label="Median" value={o.median_days} suffix="d" icon={Clock} tone="blue" />
            <Stat label="90th pct" value={o.p90_days} suffix="d" icon={Clock} tone="amber" />
            <Stat label="In progress" value={data.in_progress_total} icon={Users} tone="slate" />
          </div>

          {/* Bottleneck breakdown — where clients spend the most days */}
          {data.by_step && data.by_step.some(s => s.count > 0) && (() => {
            // Highlight the slowest step in the funnel so it stands out.
            const slowest = data.by_step
              .filter(s => s.avg_days != null)
              .sort((a, b) => (b.avg_days || 0) - (a.avg_days || 0))[0];
            return (
              <div className="border-t border-slate-100 pt-4 mb-4" data-testid="onboarding-step-breakdown">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Where clients stall
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {data.by_step.map(s => {
                    const isSlowest = slowest && s.key === slowest.key && (s.avg_days || 0) > 0;
                    return (
                      <div key={s.key}
                        data-testid={`step-breakdown-${s.key}`}
                        className={`rounded-lg border p-3 ${isSlowest ? "border-amber-300 bg-amber-50/60" : "border-slate-100"}`}>
                        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{s.label}</p>
                        <p className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {s.avg_days != null ? `${s.avg_days}` : "—"}
                          {s.avg_days != null && <span className="text-sm font-medium text-slate-500 ml-0.5">d avg</span>}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          Median {s.median_days != null ? `${s.median_days}d` : "—"} · n={s.count}
                          {isSlowest && <span className="ml-1 text-amber-700 font-semibold">· slowest</span>}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Referrer leaderboard */}
          {data.by_referrer && data.by_referrer.length > 0 && (
            <div className="border-t border-slate-100 pt-4 mb-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Fastest referrers</h4>
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-sm">
                  <thead className="text-[10px] text-slate-400 uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-2 py-1.5">Referrer</th>
                      <th className="text-right px-2 py-1.5">Completed</th>
                      <th className="text-right px-2 py-1.5">Avg</th>
                      <th className="text-right px-2 py-1.5">Median</th>
                      <th className="text-right px-2 py-1.5">In progress</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50" data-testid="onboarding-referrer-table">
                    {data.by_referrer.slice(0, 8).map((r, i) => (
                      <tr key={r.referrer_id} className="hover:bg-slate-50">
                        <td className="px-2 py-2 text-slate-800 font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            {i === 0 && r.count > 0 && <span className="text-amber-500">🏆</span>}
                            {r.referrer_name}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right text-slate-900 font-mono">{r.count}</td>
                        <td className="px-2 py-2 text-right text-slate-900 font-mono">{r.avg_days != null ? `${r.avg_days}d` : "—"}</td>
                        <td className="px-2 py-2 text-right text-slate-600 font-mono">{r.median_days != null ? `${r.median_days}d` : "—"}</td>
                        <td className="px-2 py-2 text-right text-slate-600 font-mono">{r.in_progress}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Stalled list */}
          {data.stalled_cases && data.stalled_cases.length > 0 && (
            <div className="border-t border-slate-100 pt-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Stalled cases (14d+)
              </h4>
              <div className="space-y-1" data-testid="onboarding-stalled-list">
                {data.stalled_cases.map(s => (
                  <button key={s.case_id} onClick={() => navigate(`/cases/${s.case_id}?tab=onboarding`)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded border border-amber-100 bg-amber-50/50 hover:bg-amber-100/50 text-sm text-left transition-colors"
                    data-testid={`stalled-${s.case_id}`}>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-mono text-slate-700">{s.ref}</span>
                      <span className="text-slate-500"> · {s.referrer_name} · stuck on <strong>{s.next_step}</strong></span>
                    </span>
                    <span className="shrink-0 text-xs font-semibold text-amber-700">{s.oldest_days}d</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, suffix, icon: Icon, tone }) {
  const bg = { emerald: "bg-emerald-50 text-emerald-700",
               violet: "bg-violet-50 text-violet-700",
               blue: "bg-blue-50 text-blue-700",
               amber: "bg-amber-50 text-amber-700",
               slate: "bg-slate-100 text-slate-600" }[tone] || "bg-slate-100 text-slate-600";
  return (
    <div className="border border-slate-100 rounded-lg p-3">
      <div className={`w-7 h-7 ${bg} rounded flex items-center justify-center mb-1.5`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <p className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
        {value == null ? "—" : value}{value != null && suffix ? suffix : ""}
      </p>
      <p className="text-[10px] text-slate-500 font-medium">{label}</p>
    </div>
  );
}
